import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const skillTriggerTypeSchema = z.enum([
  "manual",
  "slash_command",
  "schedule",
  "mention_keyword",
]);

export const skillVisibilitySchema = z.enum(["personal", "workspace"]);

export const createSkillBodySchema = z.object({
  workspace_id: z.string().min(1),
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  icon: z.string().max(10).optional(),
  prompt: z.string().min(1).max(10000),
  allowed_tools: z.array(z.string().min(1)).default([]),
  trigger_type: skillTriggerTypeSchema.default("manual"),
  trigger_config: z.record(z.string(), z.unknown()).default({}),
  visibility: skillVisibilitySchema.default("workspace"),
});

export const updateSkillBodySchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(1000).optional(),
  icon: z.string().max(10).optional(),
  prompt: z.string().min(1).max(10000).optional(),
  allowed_tools: z.array(z.string().min(1)).optional(),
  trigger_type: skillTriggerTypeSchema.optional(),
  trigger_config: z.record(z.string(), z.unknown()).optional(),
  visibility: skillVisibilitySchema.optional(),
  enabled: z.boolean().optional(),
});

export const skillResponseSchema = z.object({
  id: z.string().min(1),
  workspace_id: z.string().min(1),
  created_by: z.string().min(1),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  prompt: z.string(),
  allowed_tools: z.array(z.string()).default([]),
  trigger_type: z.string(),
  trigger_config: z.unknown(),
  visibility: z.string(),
  enabled: z.boolean(),
  created_at: z.number().optional(),
  updated_at: z.number().optional(),
});

export const createSkillSuccessSchema = z.object({
  skill: skillResponseSchema,
});

export const listSkillsSuccessSchema = z.object({
  skills: z.array(skillResponseSchema),
});

export const updateSkillSuccessSchema = createSkillSuccessSchema;

export type CreateSkillBody = z.infer<typeof createSkillBodySchema>;
export type UpdateSkillBody = z.infer<typeof updateSkillBodySchema>;
export type SkillResponse = z.infer<typeof skillResponseSchema>;

export function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 63);
}

function buildAuthHeaders(accessToken: string): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  };
}

export function createSkillPath(): string {
  return "/api/skills";
}

export function listSkillsPath(
  workspaceId: string,
  triggerType?: string,
): string {
  const base = `/api/skills?workspace_id=${encodeURIComponent(workspaceId)}`;
  return triggerType
    ? `${base}&trigger_type=${encodeURIComponent(triggerType)}`
    : base;
}

export function skillPath(skillId: string): string {
  return `/api/skills/${encodeURIComponent(skillId)}`;
}

export async function createSkillRequest({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  body: CreateSkillBody;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof createSkillSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = createSkillBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${createSkillPath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(parsedBody),
    },
  );
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to create skill");
  }
  const payload = await response.json().catch(() => null);
  const parsed = createSkillSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid skill response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function listSkillsRequest({
  apiBaseUrl,
  accessToken,
  workspaceId,
  triggerType,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  workspaceId: string;
  triggerType?: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof listSkillsSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${listSkillsPath(workspaceId, triggerType)}`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to list skills");
  }
  const payload = await response.json().catch(() => null);
  const parsed = listSkillsSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid skills response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function updateSkillRequest({
  apiBaseUrl,
  accessToken,
  skillId,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  skillId: string;
  body: UpdateSkillBody;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof updateSkillSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = updateSkillBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${skillPath(skillId)}`,
    {
      method: "PUT",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(parsedBody),
    },
  );
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to update skill");
  }
  const payload = await response.json().catch(() => null);
  const parsed = updateSkillSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid skill update response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function deleteSkillRequest({
  apiBaseUrl,
  accessToken,
  skillId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  skillId: string;
  fetchImpl?: FetchLike;
}): Promise<{ ok: true }> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${skillPath(skillId)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to delete skill");
  }
  return { ok: true };
}
